import React, { useState, useEffect } from 'react';

// アスペクト比の型定義
export type DownloadAspectRatio = '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | 'original';

// アスペクト比オプション
const aspectRatioOptions: { value: DownloadAspectRatio; label: string }[] = [
  { value: 'original', label: 'オリジナル' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3（横長）' },
  { value: '3:4', label: '3:4（縦長）' },
  { value: '16:9', label: '16:9（横長）' },
  { value: '9:16', label: '9:16（縦長）' },
];

interface DownloadOptionPopupProps {
  show: boolean;
  onClose: () => void;
  imageUrl: string | undefined;
  defaultFileName: string;
  onDownloadSuccess?: () => void;
  onDownloadError?: (error: Error) => void;
}

/**
 * アスペクト比に応じて画像を加工する関数
 */
const processImageWithAspectRatio = async (
  imageUrl: string,
  aspectRatio: DownloadAspectRatio
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;

      // オリジナルの場合はそのまま返す
      if (aspectRatio === 'original') {
        const canvas = document.createElement('canvas');
        canvas.width = originalWidth;
        canvas.height = originalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create blob'));
        }, 'image/png');
        return;
      }

      // アスペクト比を計算
      let targetRatioW = 1;
      let targetRatioH = 1;
      if (aspectRatio === '1:1') {
        targetRatioW = 1;
        targetRatioH = 1;
      } else if (aspectRatio === '4:3') {
        targetRatioW = 4;
        targetRatioH = 3;
      } else if (aspectRatio === '3:4') {
        targetRatioW = 3;
        targetRatioH = 4;
      } else if (aspectRatio === '16:9') {
        targetRatioW = 16;
        targetRatioH = 9;
      } else if (aspectRatio === '9:16') {
        targetRatioW = 9;
        targetRatioH = 16;
      }

      const targetRatio = targetRatioW / targetRatioH;
      const originalRatio = originalWidth / originalHeight;

      let newWidth: number;
      let newHeight: number;

      // 目標アスペクト比に合わせて、元画像が収まる最小サイズを計算
      if (originalRatio > targetRatio) {
        // 元画像が横長 → 幅に合わせて高さを拡張
        newWidth = originalWidth;
        newHeight = Math.round(originalWidth / targetRatio);
      } else {
        // 元画像が縦長 → 高さに合わせて幅を拡張
        newHeight = originalHeight;
        newWidth = Math.round(originalHeight * targetRatio);
      }

      // キャンバスを作成
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // 白で塗りつぶし
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, newWidth, newHeight);

      // 中央に元画像を配置
      const offsetX = Math.round((newWidth - originalWidth) / 2);
      const offsetY = Math.round((newHeight - originalHeight) / 2);
      ctx.drawImage(img, offsetX, offsetY, originalWidth, originalHeight);

      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

export const DownloadOptionPopup: React.FC<DownloadOptionPopupProps> = ({
  show,
  onClose,
  imageUrl,
  defaultFileName,
  onDownloadSuccess,
  onDownloadError,
}) => {
  const [downloadAspectRatio, setDownloadAspectRatio] = useState<DownloadAspectRatio>('original');
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // モーダルを開いた時、またはアスペクト比を変更した時にプレビューを生成
  useEffect(() => {
    if (!show || !imageUrl) {
      return;
    }

    let currentPreviewUrl: string | null = null;

    const generatePreview = async () => {
      setIsGeneratingPreview(true);
      try {
        const blob = await processImageWithAspectRatio(imageUrl, downloadAspectRatio);
        currentPreviewUrl = URL.createObjectURL(blob);
        setPreviewDataUrl(currentPreviewUrl);
      } catch (error) {
        console.error('プレビュー生成エラー:', error);
      } finally {
        setIsGeneratingPreview(false);
      }
    };

    generatePreview();

    // クリーンアップ: このeffectで生成したURLを解放
    return () => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
    };
  }, [show, downloadAspectRatio, imageUrl]);

  // モーダルを閉じる時に状態をリセット
  const handleClose = () => {
    setDownloadAspectRatio('original');
    setPreviewDataUrl(null);
    onClose();
  };

  // ダウンロード実行
  const executeDownload = async () => {
    if (!previewDataUrl) return;

    try {
      // ファイル名の拡張子をpngに変更（白埋めするため）
      const baseName = defaultFileName.replace(/\.[^/.]+$/, '');
      const newFileName = downloadAspectRatio === 'original'
        ? `${baseName}.png`
        : `${baseName}_${downloadAspectRatio.replace(':', 'x')}.png`;

      const link = document.createElement('a');
      link.href = previewDataUrl;
      link.download = newFileName;
      link.click();

      handleClose();
      onDownloadSuccess?.();
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      onDownloadError?.(error instanceof Error ? error : new Error('Download failed'));
    }
  };

  if (!show) return null;

  return (
    <>
      {/* 背景オーバーレイ */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 9999, backgroundColor: 'rgba(107, 114, 128, 0.75)' }}
        onClick={handleClose}
      />

      {/* モーダルコンテンツ */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div
          className="relative bg-white rounded-lg shadow-xl w-full"
          style={{ maxWidth: '600px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* コンテンツ領域 */}
          <div className="p-6">
            <h4 className="text-xl font-bold text-gray-800 mb-4">ダウンロード設定</h4>

            <div className="flex flex-col gap-4">
              {/* アスペクト比選択（プルダウン） */}
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">アスペクト比</h5>
                <select
                  value={downloadAspectRatio}
                  onChange={(e) => setDownloadAspectRatio(e.target.value as DownloadAspectRatio)}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {aspectRatioOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {downloadAspectRatio !== 'original' && (
                  <p className="mt-2 text-xs text-gray-500">
                    ※ 余白部分は白で埋められます
                  </p>
                )}
              </div>

              {/* プレビュー */}
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">プレビュー</h5>
                <div className="border border-gray-300 rounded-lg bg-gray-100 flex items-center justify-center" style={{ height: '400px' }}>
                  {isGeneratingPreview ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                      <span className="text-sm text-gray-500">プレビュー生成中...</span>
                    </div>
                  ) : previewDataUrl ? (
                    <img
                      src={previewDataUrl}
                      alt="プレビュー"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-gray-400">プレビューを読み込み中...</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* フッター */}
          <div
            className="bg-gray-50 px-6 rounded-b-lg"
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          >
            <div className="flex gap-3 justify-center" style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
              <button
                type="button"
                onClick={handleClose}
                className="py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                style={{ flex: 1 }}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={executeDownload}
                disabled={isGeneratingPreview || !previewDataUrl}
                className={`py-2 rounded transition-colors ${
                  isGeneratingPreview || !previewDataUrl
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
                style={{ flex: 1 }}
              >
                ダウンロード
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadOptionPopup;
